import React, { useState, useEffect } from 'react';
import { campaignsAPI } from '../api/campaigns';
import { gamesAPI } from '../api/games';
import CampaignKpiEditor from './CampaignKpiEditor';
import { emptyKpis, normalizeKpis } from '../utils/campaignKpis';

const EditCampaignModal = ({ isOpen, onClose, onSuccess, campaign }) => {
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [gameSearch, setGameSearch] = useState('');
  const [showGameDropdown, setShowGameDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'sponsored',
    gameId: '',
    clientName: '',
    startDate: '',
    endDate: '',
    budget: '',
    keysAvailable: '',
    status: 'planning',
    goals: '',
    notes: '',
    hashtags: '',
    kpis: emptyKpis()
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Load campaign data when modal opens
  useEffect(() => {
    if (isOpen && campaign) {
      setFormData({
        name: campaign.name || '',
        type: campaign.type || 'sponsored',
        gameId: campaign.gameId || '',
        clientName: campaign.clientName || '',
        startDate: formatDateForInput(campaign.startDate),
        endDate: formatDateForInput(campaign.endDate),
        budget: campaign.budget || '',
        keysAvailable: campaign.keysAvailable || '',
        status: campaign.status || 'planning',
        goals: campaign.goals || '',
        notes: campaign.notes || '',
        hashtags: (campaign.hashtags || []).join(', '),
        kpis: normalizeKpis(campaign.kpis)
      });
      // Set game search to show current game name
      if (campaign.game?.title) {
        setGameSearch(campaign.game.title);
      } else {
        setGameSearch('');
      }
      fetchGames();
    }
  }, [isOpen, campaign]);

  const fetchGames = async () => {
    try {
      const response = await gamesAPI.getAll({ limit: 500 });
      setGames(response.data.games);
      setFilteredGames(response.data.games);
    } catch (err) {
      console.error('Failed to fetch games:', err);
    }
  };

  const handleGameSearch = (value) => {
    setGameSearch(value);
    if (value.trim()) {
      const filtered = games.filter(g =>
        g.title.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredGames(filtered);
    } else {
      setFilteredGames(games);
    }
    setShowGameDropdown(true);
  };

  const handleGameInputFocus = async () => {
    if (games.length === 0) {
      try {
        const response = await gamesAPI.getAll({ limit: 500 });
        setGames(response.data.games);
        setFilteredGames(response.data.games);
      } catch (err) {
        console.error('Failed to fetch games:', err);
      }
    } else {
      setFilteredGames(games);
    }
    setShowGameDropdown(true);
  };

  const selectGame = (game) => {
    setGameSearch(game.title);
    setFormData({ ...formData, gameId: game.id });
    setShowGameDropdown(false);
  };

  const clearGame = () => {
    setGameSearch('');
    setFormData({ ...formData, gameId: '' });
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const dataToSend = {
        name: formData.name,
        type: formData.type,
        clientName: formData.clientName || null,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        budget: (formData.type === 'sponsored' || formData.type === 'sponsored_organic') ? parseFloat(formData.budget) || null : null,
        keysAvailable: (formData.type === 'organic' || formData.type === 'sponsored_organic') ? parseInt(formData.keysAvailable) || null : null,
        gameId: formData.gameId || null,
        status: formData.status,
        goals: formData.goals || null,
        notes: formData.notes || null,
        hashtags: formData.hashtags
          ? formData.hashtags.split(',').map(h => h.trim().replace(/^#/, '').toLowerCase()).filter(Boolean)
          : [],
        kpis: formData.kpis
      };

      await campaignsAPI.update(campaign.id, dataToSend);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Edit Campaign</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Campaign Type Toggle */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Campaign Type *
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'sponsored'
                  ? 'border-sc-pink bg-sc-pink/10'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="sponsored"
                  checked={formData.type === 'sponsored'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">💰</div>
                  <div className="font-medium">Sponsored</div>
                  <div className="text-xs text-gray-500">Paid partnerships</div>
                </div>
              </label>

              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'organic'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="organic"
                  checked={formData.type === 'organic'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">🎁</div>
                  <div className="font-medium">Organic</div>
                  <div className="text-xs text-gray-500">Key sends / No budget</div>
                </div>
              </label>

              <label className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.type === 'sponsored_organic'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="type"
                  value="sponsored_organic"
                  checked={formData.type === 'sponsored_organic'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="text-2xl mb-1">💰🎁</div>
                  <div className="font-medium">Sponsored + Organic</div>
                  <div className="text-xs text-gray-500">Budget &amp; key sends</div>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Campaign Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Campaign Name"
              />
            </div>

            {/* Game */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Game
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search games..."
                  value={gameSearch}
                  onChange={(e) => handleGameSearch(e.target.value)}
                  onFocus={handleGameInputFocus}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink pr-8"
                />
                {gameSearch && (
                  <button
                    type="button"
                    onClick={clearGame}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
              {showGameDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-10">
                  {filteredGames.length > 0 ? (
                    filteredGames.map((game) => (
                      <div
                        key={game.id}
                        onClick={() => selectGame(game)}
                        className="px-3 py-2 hover:bg-sc-pink/10 cursor-pointer text-sm"
                      >
                        {game.title}
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No games found</div>
                  )}
                </div>
              )}
            </div>

            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Client/Publisher"
              />
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              />
            </div>

            {/* Budget (for Sponsored / Sponsored + Organic) */}
            {(formData.type === 'sponsored' || formData.type === 'sponsored_organic') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Budget (USD)
                </label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="50000"
                />
              </div>
            )}

            {/* Keys Available (for Organic / Sponsored + Organic) */}
            {(formData.type === 'organic' || formData.type === 'sponsored_organic') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keys Available
                </label>
                <input
                  type="number"
                  name="keysAvailable"
                  value={formData.keysAvailable}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                  placeholder="500"
                />
              </div>
            )}

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Goals */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Goals
              </label>
              <textarea
                name="goals"
                value={formData.goals}
                onChange={handleChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Campaign goals and objectives..."
              />
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="Any additional notes..."
              />
            </div>

            {/* Campaign Hashtags */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Campaign Hashtags
              </label>
              <input
                type="text"
                name="hashtags"
                value={formData.hashtags}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sc-pink"
                placeholder="helldivers2partner, ad, sponsored (comma-separated)"
              />
              <p className="mt-1 text-xs text-gray-500">
                Content with these hashtags will be auto-linked to this campaign during scans
              </p>
            </div>

            {/* Campaign KPIs (sections shown by type) */}
            <CampaignKpiEditor
              type={formData.type}
              value={formData.kpis}
              onChange={(kpis) => setFormData({ ...formData, kpis })}
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-sc-pink text-white rounded-md hover:bg-sc-pink/90 disabled:bg-gray-400"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCampaignModal;
